<template>
  <div v-if="$apollo.queries.history.loading">
    <v-skeleton-loader
      v-if="showOrder"
      type="button"
      loading
      class="d-inline-block mr-2"
    />
    <v-skeleton-loader
      v-if="showFilters"
      type="button"
      loading
      class="d-inline-block"
    />
    <v-skeleton-loader type="image" loading class="mt-2" />
  </div>
  <div v-else>
    <v-row v-if="showOrder || showFilters" no-gutters>
      <!-- settings -->
      <v-col v-if="showOrder" cols="12" lg="6">
        <v-row
          class="ma-0"
          :justify="$vuetify.breakpoint.lgAndUp ? 'start' : 'center'"
        >
          <!-- layout -->
          <v-btn-toggle v-model="selectedLayout" dense mandatory>
            <v-btn v-for="item in layouts" :key="item.code" :value="item.value">
              {{ item.text }}
              <v-icon right>{{ item.icon }}</v-icon>
            </v-btn>
          </v-btn-toggle>

          <!-- order -->
          <v-btn-toggle v-model="selectedOrder" dense mandatory class="ml-2">
            <v-btn v-for="item in orders" :key="item.code" :value="item.value">
              {{ item.text }}
              <v-icon right>{{ item.icon }}</v-icon>
            </v-btn>
          </v-btn-toggle>
        </v-row>
      </v-col>

      <!-- filters -->
      <template v-if="filterAvailable && showFilters">
        <v-col cols="12" lg="6" class="text-right">
          <v-chip-group
            v-model="selectedFilters"
            multiple
            show-arrows
            v-bind="$attrs"
          >
            <v-spacer />
            <v-chip
              v-for="(filter, index) in filters"
              :key="index"
              filter
              small
              outlined
              :value="index"
            >
              <v-icon v-if="filter.icon" small left>{{ filter.icon }}</v-icon>
              {{ filter.text }}
              ({{ getEventCount(filter.events) }})
            </v-chip>
            <v-spacer v-if="$vuetify.breakpoint.mdAndDown" />
          </v-chip-group>
        </v-col>
      </template>
    </v-row>

    <div v-if="filterActive">
      <v-alert v-bind="$attrs" type="warning" text dense class="mb-0">
        {{ $t('fresh-vue-history.misc.filter-active') }}
      </v-alert>
    </div>

    <v-fade-transition leave-absolute>
      <!-- TABLE -->
      <history-view-table
        v-if="selectedLayout === 'TABLE'"
        key="TABLE"
        :history="filteredHistory"
        :show-duration="showDuration"
      >
        <!-- passthrough slots -->
        <slot v-for="(_, name) in $slots" :slot="name" :name="name" />
        <template
          v-for="(_, name) in $scopedSlots"
          :slot="name"
          slot-scope="slotData"
        >
          <slot :name="name" v-bind="slotData" />
        </template>
      </history-view-table>

      <!-- TIMELINE -->
      <history-view-timeline
        v-else-if="selectedLayout === 'TIMELINE'"
        key="TIMELINE"
        :history="filteredHistory"
        :show-duration="showDuration"
        :order="selectedOrder"
      >
        <!-- passthrough slots -->
        <slot v-for="(_, name) in $slots" :slot="name" :name="name" />
        <template
          v-for="(_, name) in $scopedSlots"
          :slot="name"
          slot-scope="slotData"
        >
          <slot :name="name" v-bind="slotData" />
        </template>
      </history-view-timeline>

      <!-- OTHER -->
      <div v-else key="OTHER" cols="12">
        {{ $t('fresh-vue-history.misc.no-layout') }}
      </div>
    </v-fade-transition>
  </div>
</template>

<script>
import gql from 'graphql-tag'

import { AUDIT as FRAGMENT_AUDIT } from '@freshsolutions/fresh-vue-audits/src/graphql/fragments.js'
import { NOTE_SIMPLE as FRAGMENT_NOTE_SIMPLE } from '@freshsolutions/fresh-vue-notes/src/graphql/fragments.js'

import HistoryViewTable from './HistoryViewTable'
import HistoryViewTimeline from './HistoryViewTimeline'

export default {
  name: 'HistoryView',

  components: {
    HistoryViewTable,
    HistoryViewTimeline
  },

  props: {
    historieableId: {
      type: Number,
      required: true
    },

    historieableType: {
      type: String,
      required: true
    },

    showDuration: {
      type: Boolean,
      required: false,
      default: false
    },

    layout: {
      type: String,
      required: false,
      default: 'TIMELINE',
      validator: (value) => ['TABLE', 'TIMELINE'].includes(value)
    },

    order: {
      type: String,
      required: false,
      default: 'DESC',
      validator: (value) => ['DESC', 'ASC'].includes(value)
    },

    hideFilters: {
      type: Boolean,
      required: false,
      default: false
    },

    additionalFilters: {
      type: Array,
      required: false,
      default: null
    },

    showOrder: {
      type: Boolean,
      required: false,
      default: true
    },

    showFilters: {
      type: Boolean,
      required: false,
      default: true
    },

    predefinedFilters: {
      type: Array,
      required: false,
      default: null
    },

    defaultView: {
      type: String,
      required: false,
      default: null
    }
  },

  apollo: {
    history: {
      query: gql`
        query histories(
          $historieable_id: Int!
          $historieable_type: String!
          $order: SortOrder!
        ) {
          history: histories(
            historieable_id: $historieable_id
            historieable_type: $historieable_type
            orderBy: [
              { column: CREATED_AT, order: $order }
              { column: ID, order: $order } # Sometimes there are multiple events at the same time. So we will order by the ID after sorting by the created_at date.
            ]
          ) {
            id

            historieable_id
            historieable_type

            event
            duration
            description
            data

            audit {
              ...Audit
            }
            note {
              ...NoteSimple
            }

            created_at
            created_by {
              id
              name
            }

            __typename
          }
        }
        ${FRAGMENT_AUDIT}
        ${FRAGMENT_NOTE_SIMPLE}
      `,
      variables() {
        return {
          historieable_id: this.historieableId,
          historieable_type: this.historieableType,
          order: this.selectedOrder
        }
      },
      update: (data) => {
        return (data.history || []).map((item) => ({
          ...item,
          data: JSON.parse(item.data) // we want to have JSON here directly
        }))
      },
      fetchPolicy: 'network-only',
      pollInterval: 5000
    }
  },

  data() {
    return {
      layouts: [
        {
          text: 'Timeline',
          value: 'TIMELINE',
          icon: '$freshvuehistory-view-timeline'
        },
        { text: 'Tabelle', value: 'TABLE', icon: '$freshvuehistory-view-table' }
      ],
      selectedLayout: 'TIMELINE',

      orders: [
        {
          text: 'Neu oben',
          value: 'DESC',
          icon: '$freshvuehistory-order-desc'
        },
        { text: 'Alt oben', value: 'ASC', icon: '$freshvuehistory-order-asc' }
      ],
      selectedOrder: 'DESC',

      defaultFilters: [
        {
          id: 'EDITS',
          text: 'Bearbeitungen',
          icon: '$freshvuehistory-filter-EDIT',
          events: ['CREATED', 'UPDATED', 'DELETED', 'RESTORED']
        },
        {
          id: 'NOTES',
          text: 'Notizen',
          icon: '$freshvuehistory-filter-NOTE',
          events: ['NOTE_CREATED', 'NOTE_DELETED']
        }
      ],

      history: [],
      selectedFilters: []
    }
  },

  computed: {
    filteredHistory() {
      if (this.predefinedFilters?.length > 0) {
        let events = []
        for (let predefinedFilter of this.predefinedFilters) {
          for (let filter of this.filters) {
            if (filter.id === predefinedFilter) {
              for (let event of filter.events) {
                events.push(event)
              }
            }
          }
        }
        return this.getHistoryItemsFilteredByEvents(events)
      } else {
        // get the events which should be filtered by
        const events = this.selectedFilters.flatMap(
          (index) => this.filters[index].events
        )
        return this.getHistoryItemsFilteredByEvents(events)
      }
    },

    filterAvailable() {
      return !this.hideFilters && this.filters?.length > 0
    },

    filterActive() {
      return this.selectedFilters.length > 0
    },

    filters() {
      return []
        .concat(this.defaultFilters || [])
        .concat(this.additionalFilters || [])
    }
  },

  watch: {
    layout: {
      handler(value) {
        if (
          (this.defaultView && !this.selectedLayout) ||
          (this.defaultView && !this.showFilters)
        ) {
          this.selectedLayout = this.defaultView
        } else {
          this.selectedLayout = value
        }
      },
      immediate: true
    },

    order: {
      handler(value) {
        this.selectedOrder = value
      },
      immediate: true
    },

    selectedLayout(value) {
      this.$emit('update:layout', value)
    },

    selectedOrder(value) {
      this.$emit('update:order', value)
    }
  },

  methods: {
    refetch() {
      this.$apollo.queries.history.refetch()
    },

    getHistoryItemsFilteredByEvents(events) {
      let items = this.history
      if (events && events.length > 0) {
        // we need to use "endsWith" to catch the default events for the ParentHistorieable
        items = items.filter((item) =>
          events.some((event) => item.event.endsWith(event))
        )
      }
      return items
    },

    getEventCount(events) {
      return this.getHistoryItemsFilteredByEvents(events).length
    }
  }
}
</script>
